import { useState } from "react";
import styled, { useTheme } from "styled-components";

import {
  FolderPlus,
  ChevronDown,
  ChevronRight,
  FilePlus,
  ArrowRightIcon,
} from "lucide-react";
import { useNoteSystem } from "../Contexts/NoteSystemContext.js";
import TaskButton from "@/Monolith-UI/TaskButton.js";
import { EntityInfoTooltip } from "@/components/EntityInfo";

const NoteSegment = styled(
  ({
    className,
    children,
    label,
    readOnly = false,
    defaultOpen = false,
    handleCreateNote,
    handleCreateFolder,
    object_type,
    object_name,
    object_id,
    hideObjectTypeBadge = false,
    onDragOver = () => {},
  }) => {
    const [open, setOpen] = useState(defaultOpen);
    const [isOver, setIsOver] = useState(false);

    const { currentNoteRef } = useNoteSystem();

    const theme = useTheme();

    const toggleOpen = () => {
      setOpen((prev) => !prev);
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      setIsOver(true);
      onDragOver?.({
        object_name,
        object_type,
        object_id,
      });
    };

    const handleDragLeave = (e) => {
      setIsOver(false);
    };

    const handleHeaderClick = (e) => {
      toggleOpen();
    };

    const handleFileClick = (e) => {
      e.stopPropagation();

      let parent_id = null;

      // Check if the currentNoteRef is in the same object as the segment
      if (currentNoteRef?.current?.object_id !== object_id) {
        parent_id = null;
      } else {
        parent_id =
          currentNoteRef?.current?.is_folder === 1
            ? currentNoteRef?.current?.uuid
            : currentNoteRef?.current?.parent_id || null;
      }

      handleCreateNote?.({
        object_name,
        object_type,
        object_id,
        parent_id,
      });
    };

    const handleFolderClick = (e) => {
      e.stopPropagation();
      handleCreateFolder?.({
        object_name,
        object_type,
        object_id,
      });
    };

    return (
      <div
        className={className}
        data-is-over={isOver}
        draggable={false}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="header" data-open={open} onClick={handleHeaderClick}>
          {open ? (
            <ChevronDown size={16} style={{ marginRight: 5 }} />
          ) : (
            <ChevronRight size={16} style={{ marginRight: 5 }} />
          )}
          <EntityInfoTooltip type={object_type} id={object_id}>
            <div className="entity-link" role="button" tabIndex={0}>
              {!hideObjectTypeBadge && (
                <div className="object-type" data-object-type={object_type}>
                  {object_type}
                </div>
              )}
              <div className="label">{label}</div>
              <ArrowRightIcon size={12} className="arrow-icon" aria-hidden />
            </div>
          </EntityInfoTooltip>
          {!readOnly && open && (
            <div className="toolbar">
              <TaskButton
                onClick={handleFileClick}
                title="Create Note"
                variant="text"
              >
                <FilePlus
                  size={16}
                  strokeWidth={1}
                  absoluteStrokeWidth
                  color={`${theme.palette.primary.main}`}
                />
              </TaskButton>
              <TaskButton
                onClick={handleFolderClick}
                title="Create Directory"
                variant="text"
                className="folder-button"
              >
                <FolderPlus size={16} color={`${theme.palette.warning.main}`} />
              </TaskButton>
            </div>
          )}
        </div>
        {open && <div className="content">{children}</div>}
      </div>
    );
  }
)`
  margin-bottom: 5px;
  &:hover {
    .toolbar {
      visibility: visible;
    }
  }

  .entity-link {
    display: flex;
    align-items: center;
    cursor: pointer;
    min-width: 0;

    .label {
      max-width: 280px;
      min-width: 0;
      padding: 1px 6px;
      border-radius: 3px;
      background-color: ${({ theme }) => theme.palette.background.secondary};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .arrow-icon {
      visibility: hidden;
      flex-shrink: 0;
      margin-left: 4px;
      color: ${({ theme }) => theme.palette.text.secondary};
    }

    &:hover,
    &:focus-visible {
      .arrow-icon {
        visibility: visible;
      }
      .label {
        background-image: linear-gradient(
            ${({ theme }) => theme.palette.action.hover},
            ${({ theme }) => theme.palette.action.hover}
          ),
          linear-gradient(
            ${({ theme }) => theme.palette.action.hover},
            ${({ theme }) => theme.palette.action.hover}
          );
      }
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.palette.primary.main};
      outline-offset: 1px;
      border-radius: 3px;
    }
  }

  .object-type {
    font-size: 0.6rem;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.text.secondary};
    margin-right: 5px;

    padding: 1px 3px;
    border-radius: 3px;
    background-color: ${({ theme }) => theme.palette.action.hover};
    text-transform: capitalize;

    &[data-object-type="case"] {
      background-color: ${({ theme }) => theme.palette.primary.main}35;
      color: ${({ theme }) => theme.palette.primary.main};
    }

    &[data-object-type="evidence"] {
      background-color: ${({ theme }) => theme.palette.success.main}35;
      color: ${({ theme }) => theme.palette.success.main};
    }
  }

  .header {
    user-select: none;
    display: flex;
    align-items: center;
    padding-right: 5px;
    padding-top: 5px;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.text.secondary};

    svg {
      font-weight: 400;
      &:hover {
        background-color: ${({ theme }) => theme.palette.action.hover};
      }
    }
  }

  // First header item
  &:first-child .header {
    padding-top: 0px;
  }

  .label {
    font-size: 0.75rem;
    font-weight: bold;
    color: ${({ theme }) => theme.palette.text.secondary};
    text-transform: capitalize;
  }

  .content {
    pointer-events: none;
    padding-left: 10px;
  }

  .toolbar {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
    margin-left: auto;
    visibility: hidden;

    .task-button:hover {
      border: 1px solid transparent;
      background-color: ${({ theme }) => theme.palette.action.hover};
    }
  }
`;

export default NoteSegment;
